import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import type { FieldReportFullViewFilterContextType } from 'af-root/context/fieldReportFullViewFilterContext';

import type { W_WorkRequest_FindAllForFieldReportFilter_VM_WorkRequest } from 'ab-api/web/workRequest/findAllForFieldReportFilter';

import AdditionalFilter from 'af-components/Table/Filters/AdditionalFilter/Multiselect';

import * as WorkRequestActions from 'af-actions/workRequests';

import JobOption from './Shared/JobOption';

export class JobFilterOption {
	id: number;
	/** Will technically be job code, but just duplicating for display purposes */
	label: string;
	jobCode: string;
	title: string;
	customerCompany: Nullable<string>;
	isInternal: boolean;

	constructor(job: W_WorkRequest_FindAllForFieldReportFilter_VM_WorkRequest) {
		this.id = job.id;
		this.title = job.title;
		this.label = job.jobCode;
		this.jobCode = job.jobCode;
		this.customerCompany = job.customerCompany;
		this.isInternal = job.isInternal;
	}

	static bulkConstructor = (_jobs: W_WorkRequest_FindAllForFieldReportFilter_VM_WorkRequest[]) => _jobs.map((_job) => JobFilterOption._constructorMap(_job));

	private static _constructorMap = (_job: W_WorkRequest_FindAllForFieldReportFilter_VM_WorkRequest) => new JobFilterOption(_job);
}

interface OwnProps {
	onChange: (options: JobFilterOption[]) => void;
	selected: JobFilterOption[];
	forBulkModal?: boolean;
	onClear?: () => void;
	autoOpen?: boolean;
	fieldReportFullViewFilterContext: FieldReportFullViewFilterContextType;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const JobFilter: React.FC<Props> = (props) => {

	const {
		onChange,
		findAllForFieldReportFilter,
		selected,
		forBulkModal,
		onClear,
		autoOpen,
		fieldReportFullViewFilterContext,
	} = props;

	const [options, setOptions] = React.useState<JobFilterOption[] | null>(null);

	const fetchOptions = React.useCallback(async () => {
		const _options = await findAllForFieldReportFilter();
		const formattedOptions = JobFilterOption.bulkConstructor(_options.workRequests);
		setOptions(formattedOptions);
		fieldReportFullViewFilterContext.setJobOptions(formattedOptions);
	}, [fieldReportFullViewFilterContext, findAllForFieldReportFilter]);

	React.useEffect(() => {
		if (!fieldReportFullViewFilterContext.jobOptions.length) {
			fetchOptions();
		} else {
			setOptions(fieldReportFullViewFilterContext.jobOptions);
		}
	}, [fetchOptions, fieldReportFullViewFilterContext.jobOptions, findAllForFieldReportFilter]);

	const renderOption = React.useCallback((option: JobFilterOption) => {
		return (
			<JobOption
				customerCompany={option.customerCompany}
				isInternal={option.isInternal}
				jobCode={option.jobCode}
				title={option.title}
			/>
		);
	}, []);

	const onValueChange = React.useCallback((_options: JobFilterOption[]) => {
		onChange(_options);
	}, [onChange]);

	return (
		<AdditionalFilter
			autoOpen={!!autoOpen}
			filterName="Job"
			forBulkModal={!!forBulkModal}
			onChange={onValueChange}
			onRemoveFilter={onClear}
			options={options}
			renderOptionOverride={renderOption}
			selected={selected}
		/>
	);
};

function mapDispatchToProps() {
	return {
		findAllForFieldReportFilter: WorkRequestActions.findAllForFieldReportFilter,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(JobFilter);
